function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function clampNonNeg(n) {
  return n > 0 ? n : 0;
}

function volume(b) {
  return clampNonNeg(b.l) * clampNonNeg(b.w) * clampNonNeg(b.h);
}

function footprintKey(l, w) {
  const a = Math.min(l, w);
  const b = Math.max(l, w);
  return `${a.toFixed(6)}x${b.toFixed(6)}`;
}

function chooseStackOrientation(box, truckL, truckW, allowRotateLW) {
  // Pick a stable base orientation for stacking. Prefer orientation that fits, then smaller width (better packing), then smaller length.
  const options = [
    { l: box.l, w: box.w, rotated: false },
    ...(allowRotateLW && Math.abs(box.l - box.w) > 1e-9 ? [{ l: box.w, w: box.l, rotated: true }] : []),
  ].filter((o) => o.l <= truckL + 1e-9 && o.w <= truckW + 1e-9);

  if (!options.length) return null;
  options.sort((a, b) => a.w - b.w || a.l - b.l);
  return options[0];
}

function rectArea(r) {
  return clampNonNeg(r.l) * clampNonNeg(r.w);
}

function splitFreeRects(freeRects, placed) {
  // Guillotine split: right + top.
  // placed: {x,y,l,w}
  const next = [];
  for (const fr of freeRects) {
    const overlapX0 = Math.max(fr.x, placed.x);
    const overlapY0 = Math.max(fr.y, placed.y);
    const overlapX1 = Math.min(fr.x + fr.l, placed.x + placed.l);
    const overlapY1 = Math.min(fr.y + fr.w, placed.y + placed.w);
    const overlaps = overlapX1 > overlapX0 && overlapY1 > overlapY0;
    if (!overlaps) {
      next.push(fr);
      continue;
    }

    // Right remainder
    const rightX = placed.x + placed.l;
    const rightW = fr.x + fr.l - rightX;
    if (rightW > 1e-9) {
      next.push({
        x: rightX,
        y: fr.y,
        l: rightW,
        w: fr.w,
      });
    }

    // Top remainder
    const topY = placed.y + placed.w;
    const topH = fr.y + fr.w - topY;
    if (topH > 1e-9) {
      next.push({
        x: fr.x,
        y: topY,
        l: fr.l,
        w: topH,
      });
    }
  }

  // Filter tiny rectangles and dedupe-ish by rounding.
  return next
    .filter((r) => r.l > 1e-6 && r.w > 1e-6)
    .sort((a, b) => rectArea(b) - rectArea(a));
}

function pickPlacement(freeRects, box, allowRotateLW) {
  // Choose the placement that leaves least waste in a free rectangle.
  // Returns { x, y, l, w, rotated } or null.
  const candidates = [];
  const orientations = [
    { l: box.l, w: box.w, rotated: false },
    ...(allowRotateLW && Math.abs(box.l - box.w) > 1e-9 ? [{ l: box.w, w: box.l, rotated: true }] : []),
  ];

  for (const fr of freeRects) {
    for (const o of orientations) {
      if (o.l <= fr.l + 1e-9 && o.w <= fr.w + 1e-9) {
        const waste = rectArea(fr) - (o.l * o.w);
        candidates.push({
          x: fr.x,
          y: fr.y,
          l: o.l,
          w: o.w,
          rotated: o.rotated,
          waste,
          frArea: rectArea(fr),
        });
      }
    }
  }

  if (!candidates.length) return null;
  candidates.sort((a, b) => a.waste - b.waste || a.frArea - b.frArea || a.y - b.y || a.x - b.x);
  const best = candidates[0];
  return { x: best.x, y: best.y, l: best.l, w: best.w, rotated: best.rotated };
}

function packShelfRows(items, truckL, truckW) {
  // Deterministic shelf/row packing that prioritizes FILLING WIDTH before LENGTH:
  // - Maintain multiple fixed-height rows (row height = first item width in that row)
  // - For each item, consider placing into any compatible row or starting a new row.
  // - Choose the placement that minimizes the maximum used length across all rows (spread across width first).
  //
  // Coordinate system:
  // - X: back -> front (0..truckL)
  // - Y: left -> right (0..truckW)
  //
  // Each item is assumed to already have final base orientation (l,w).
  const placed = [];
  const unplaced = [];

  const rows = []; // [{ y, rowW, x }]
  const usedWidth = () => rows.reduce((s, r) => s + r.rowW, 0);
  const maxRowX = () => rows.reduce((m, r) => Math.max(m, r.x), 0);

  for (const it of items) {
    const l = num(it.l);
    const w = num(it.w);
    if (!(l > 0 && w > 0)) {
      unplaced.push(it);
      continue;
    }

    const currentMaxX = maxRowX();

    // Build candidate placements: existing rows + potential new row.
    const candidates = [];

    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (w > r.rowW + 1e-9) continue;
      if (r.x + l > truckL + 1e-9) continue;
      const newX = r.x + l;
      const maxXAfter = Math.max(currentMaxX, newX);
      candidates.push({
        kind: 'existing',
        rowIdx: i,
        maxXAfter,
        rowXAfter: newX,
        // Tie-break: keep row lengths balanced (smaller rowXAfter) then earlier rows.
        tieA: newX,
        tieB: i,
      });
    }

    // Candidate: start a new row (only if width allows).
    const y0 = usedWidth();
    if (y0 + w <= truckW + 1e-9 && l <= truckL + 1e-9) {
      const maxXAfter = Math.max(currentMaxX, l);
      candidates.push({
        kind: 'new',
        rowIdx: rows.length,
        maxXAfter,
        rowXAfter: l,
        // Tie-break: prefer creating rows early (fill width), then shorter row.
        tieA: -1,
        tieB: l,
      });
    }

    if (!candidates.length) {
      unplaced.push(it);
      continue;
    }

    candidates.sort((a, b) => a.maxXAfter - b.maxXAfter || a.tieA - b.tieA || a.tieB - b.tieB);
    const best = candidates[0];

    if (best.kind === 'new') {
      rows.push({ y: y0, rowW: w, x: 0 });
    }

    const row = rows[best.rowIdx];
    placed.push({ ...it, x: row.x, y: row.y });
    row.x += l;
  }

  return { placed, unplaced };
}

/**
 * Heuristic 3D packing: layered 2D guillotine packing.
 *
 * - Rotation: swap L/W only (floor rotation).
 * - Heaviest items are placed first, ensuring lower layers are heavier.
 *
 * @param {object} input
 * @param {{ length_feet: number, width_feet: number, height_feet: number, capacity_tons?: number }} input.truck
 * @param {Array<{ id: string|number, l: number, w: number, h: number, weight_lbs: number, meta?: any }>} input.boxes
 * @param {{ allowRotateLW?: boolean }} [input.options]
 */
export function packTruck({ truck, boxes, options = {} }) {
  const allowRotateLW = options.allowRotateLW !== false;
  const truckL = num(truck?.length_feet);
  const truckW = num(truck?.width_feet);
  const truckH = num(truck?.height_feet);
  const capacityTons = truck?.capacity_tons == null ? null : num(truck.capacity_tons);

  const reasons = [];
  if (!(truckL > 0 && truckW > 0 && truckH > 0)) {
    reasons.push('Missing truck dimensions (length/width/height).');
  }

  const rawBoxes = Array.isArray(boxes) ? boxes : [];
  const expanded = rawBoxes
    .map((b) => ({
      id: b.id,
      l: num(b.l),
      w: num(b.w),
      h: num(b.h),
      weight_lbs: num(b.weight_lbs),
      meta: b.meta,
    }))
    .filter((b) => b.l > 0 && b.w > 0 && b.h > 0);

  const totalWeightLbs = expanded.reduce((s, b) => s + num(b.weight_lbs), 0);
  const totalWeightTons = totalWeightLbs / 2000;
  if (capacityTons != null && capacityTons > 0 && totalWeightTons > capacityTons + 1e-9) {
    reasons.push(`Exceeds truck max weight (${totalWeightTons.toFixed(2)} tons > ${capacityTons.toFixed(2)} tons).`);
  }

  // Quick per-box fit check (ignores stacking).
  if (truckL > 0 && truckW > 0 && truckH > 0) {
    for (const b of expanded) {
      const fitsFloor = (b.l <= truckL + 1e-9 && b.w <= truckW + 1e-9) ||
        (allowRotateLW && b.w <= truckL + 1e-9 && b.l <= truckW + 1e-9);
      if (!fitsFloor) {
        reasons.push(`Item ${b.id} does not fit on truck floor (needs ${b.l.toFixed(2)}×${b.w.toFixed(2)} ft).`);
        break;
      }
      if (b.h > truckH + 1e-9) {
        reasons.push(`Item ${b.id} height exceeds truck height (${b.h.toFixed(2)} ft > ${truckH.toFixed(2)} ft).`);
        break;
      }
    }
  }

  // If missing truck dims, we can’t pack; return early but still report weight.
  if (reasons.some((r) => r.startsWith('Missing truck dimensions'))) {
    return {
      fits: false,
      reasons,
      weight: { total_lbs: totalWeightLbs, total_tons: totalWeightTons, capacity_tons: capacityTons },
      used: { length_feet: 0, width_feet: 0, height_feet: 0 },
      wastedPct: null,
      layers: [],
    };
  }

  // Sort: heaviest first, then volume.
  expanded.sort((a, b) => b.weight_lbs - a.weight_lbs || volume(b) - volume(a));

  // Step 1: Pre-stack identical footprints into vertical stacks (reduces wasted floor space).
  // We only stack items that share the same (L/W) footprint after choosing a stable floor orientation.
  const groups = new Map(); // key -> array of boxes with chosen {l,w,rotated}
  for (const b of expanded) {
    const orient = chooseStackOrientation(b, truckL, truckW, allowRotateLW);
    if (!orient) continue;
    const key = `${footprintKey(orient.l, orient.w)}|h=${b.h.toFixed(6)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ ...b, l: orient.l, w: orient.w, rotated: orient.rotated });
  }

  const stacks = []; // [{ baseL, baseW, pallets:[{...b}], height }]
  for (const arr of groups.values()) {
    // Already heavy->light because expanded was sorted and we iterated in order.
    let current = null;
    let hSum = 0;
    for (const b of arr) {
      if (!current) {
        current = { baseL: b.l, baseW: b.w, pallets: [], height: 0 };
        hSum = 0;
      }
      if (hSum + b.h > truckH + 1e-9) {
        if (current.pallets.length) {
          current.height = hSum;
          stacks.push(current);
        }
        current = { baseL: b.l, baseW: b.w, pallets: [], height: 0 };
        hSum = 0;
      }
      current.pallets.push(b);
      hSum += b.h;
    }
    if (current && current.pallets.length) {
      current.height = hSum;
      stacks.push(current);
    }
  }

  // Step 2: Pack stacks on the floor (2D). Each stack is a single base rectangle.
  // Sort stacks by base area (desc), then height (desc), then total weight (desc).
  const stackBoxes = stacks.map((s, idx) => {
    const totalW = s.pallets.reduce((sum, p) => sum + p.weight_lbs, 0);
    return {
      id: `stack-${idx + 1}`,
      l: s.baseL,
      w: s.baseW,
      h: s.height,
      weight_lbs: totalW,
      meta: { stackIndex: idx, pallets: s.pallets },
    };
  });

  // For row packing: sort by width desc (create tall/wide rows first), then length desc, then weight.
  stackBoxes.sort((a, b) => b.w - a.w || b.l - a.l || b.weight_lbs - a.weight_lbs);

  let maxUsedL = 0;
  let maxUsedW = 0;

  const { placed: placedStacks, unplaced } = packShelfRows(stackBoxes, truckL, truckW);
  for (const ps of placedStacks) {
    maxUsedL = Math.max(maxUsedL, Number(ps.x || 0) + Number(ps.l || 0));
    maxUsedW = Math.max(maxUsedW, Number(ps.y || 0) + Number(ps.w || 0));
  }

  // Step 3: Expand placed stacks back into pallet placements with per-pallet z0.
  // Build layers as unique z0 slices.
  const palletPlacements = [];
  for (const ps of placedStacks) {
    const pallets = ps.meta?.pallets || [];
    let z0 = 0;
    for (const p of pallets) {
      palletPlacements.push({
        id: p.id,
        x: ps.x,
        y: ps.y,
        l: ps.l,
        w: ps.w,
        h: p.h,
        rotated: p.rotated,
        weight_lbs: p.weight_lbs,
        meta: p.meta,
        z0,
      });
      z0 += p.h;
    }
  }

  // If we couldn't place all stacks, mark not fit.
  if (unplaced.length) {
    reasons.push(`Does not fit: ${unplaced.length} stack(s) could not be placed on the truck floor.`);
  }

  // Build layers by z0.
  const zSet = Array.from(new Set(palletPlacements.map((p) => Number(p.z0 || 0)))).sort((a, b) => a - b);
  const layers = zSet.map((z, idx) => {
    const placements = palletPlacements.filter((p) => Number(p.z0 || 0) === z);
    const height = placements.reduce((m, p) => Math.max(m, Number(p.h || 0)), 0);
    return {
      index: idx + 1,
      z0: z,
      height,
      placements: placements.map(({ z0: _z0, ...rest }) => rest),
    };
  });

  const usedHeight = palletPlacements.reduce((m, p) => Math.max(m, (Number(p.z0 || 0) + Number(p.h || 0))), 0);
  const used = {
    length_feet: maxUsedL,
    width_feet: maxUsedW,
    height_feet: usedHeight,
  };

  if (unplaced.length) {
    reasons.push(`Does not fit: ${unplaced.length} pallet(s) could not be placed within truck dimensions.`);
  }
  if (usedHeight > truckH + 1e-9) {
    reasons.push(`Exceeds truck height (${usedHeight.toFixed(2)} ft > ${truckH.toFixed(2)} ft).`);
  }

  const truckVolume = truckL * truckW * truckH;
  const usedVolume = palletPlacements.reduce((s, p) => s + (p.l * p.w * p.h), 0);
  const wastedPct = truckVolume > 0 ? Math.max(0, (1 - usedVolume / truckVolume) * 100) : null;

  return {
    fits: reasons.length === 0,
    reasons,
    weight: { total_lbs: totalWeightLbs, total_tons: totalWeightTons, capacity_tons: capacityTons },
    used,
    wastedPct,
    layers,
    unplaced: unplaced.map((b) => b.id),
    truck: { length_feet: truckL, width_feet: truckW, height_feet: truckH },
  };
}

